import React, {useEffect, useRef, useState} from 'react';
import {Typography} from '@mui/material';
import {API_ENDPOINTS} from "../../../apiConfig";
import API from "../../../utils/API";
import Error from "../../../components/Error";
import Forms from "../../../components/Forms";
import {useTranslation} from 'react-i18next';
import {slugify} from "../../../utils/tools";


const ConcertModal = ({close, onConcertUpdate, concert}) => {
    const {t} = useTranslation();
    const inputRef = useRef(null);
    const [errors, setErrors] = useState({});

    const [location, setLocation] = useState(concert?.location || '');
    const [date, setDate] = useState(concert?.date || '');
    const [description, setDescription] = useState(concert?.description || '');
    const [ticketUrl, setTicketUrl] = useState(concert?.ticketUrl || '');
    const [slug, setSlug] = useState('');
    const [slugDirty, setSlugDirty] = useState(true);

    const [error, setError] = useState('');

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 1);
    }, []);

    useEffect(() => {
        // při přepnutí na jiný záznam v rámci stejného modálu
        setLocation(concert?.location || '');
        setDescription(concert?.description || '');
        setTicketUrl(concert?.ticketUrl || '');
        setDate(concert?.date || '');

        // slug z existujícího záznamu
        const s = concert?.slug || '';
        setSlug(s);
        setSlugDirty(Boolean(s)); // pokud má slug, považujeme ho za ručně nastavený
    }, [concert]);

    // Auto-generování slugu z title, pokud nebyl ručně měněn
    useEffect(() => {
        if (!slugDirty && location) {
            setSlug(slugify(location));
        }
    }, [location, slugDirty]);

    const isEdit = Boolean(concert?._id);

    // handler pro ruční změnu slugu
    const handleSlugChange = (val) => {
        setSlug(val);
        setSlugDirty(val.length > 0 ? true : false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (Object.keys(errors).length) return;

        const payload = {
            location: location.trim(),
            date,
            description: description.trim(),
            ticketUrl: ticketUrl.trim() || undefined,
            slug: slug.trim()
        };

        try {
            if (isEdit) {
                await API.put(API_ENDPOINTS.concerts.edit(concert._id), payload);
            } else {
                await API.post(API_ENDPOINTS.concerts.create, payload);
            }
            onConcertUpdate?.();
            handleClose();
        } catch (err) {
            const errorData = err.response.data;
            if (errorData.errorCode === 'mongoDocumentDuplicityError' && errorData.error.keyPattern.slug) {
                err.response.data.errorCode = 'URLDuplicityError';
            }
            setError(err);
            console.error("Failed to upsert concert:", err);
        }
    };

    const handleClose = () => {
        close();
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="h6" component="h2" className="mb-2">
                {isEdit ? t('concerts.editConcert') : t('concerts.addConcert')}
            </Typography>

            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                <Forms.Text
                    ref={inputRef}
                    required
                    placeholder={t('concerts.location')}
                    label={t('concerts.location')}
                    value={location}
                    onChange={setLocation}
                    error={errors.location}
                    className="rounded-md"
                />

                {/* slug */}
                <Forms.Text
                    required
                    label={t('concerts.slug')}
                    placeholder={t('concerts.slug')}
                    value={slug}
                    onChange={handleSlugChange}
                />

                <Forms.DateTime
                    required
                    label={t('concerts.date')}
                    value={date}
                    onChange={setDate}
                    error={errors.date}
                    helperText={t('concerts.dateHelp') /* např. "DD.MM.RRRR nebo YYYY-MM-DD" */}
                    className="rounded-md"
                />
                <Forms.Textarea
                    required
                    placeholder={t('concerts.description')}
                    label={t('concerts.description')}
                    value={description}
                    onChange={setDescription}
                    error={errors.description}
                    rows={4}
                    className="rounded-md col-span-2"
                />
                <Forms.Text
                    placeholder="https://…"
                    label={t('concerts.ticketUrl')}
                    value={ticketUrl}
                    onChange={setTicketUrl}
                    error={errors.ticketUrl}
                    className="rounded-md col-span-2"
                />
            </div>

            {error && <Error error={error}/>}

            <Forms.Button submit label={isEdit ? t('save') : t('save')} className="mt-4"/>
        </form>
    );
};

export default ConcertModal;
